'use client';

import { useState, useMemo, FormEventHandler } from 'react';
import { useMediaQuery } from 'react-responsive';
import { DrawerDialog } from '@/components/core/modals';
import { Button, CheckboxWithLabel, Flex } from '@instamenu/ui-system-next';
import { useLocale } from '@instamenu/context-i18n';
import { styledItem, styledRow } from './RestaurantSelection.css';
import { useUserContext } from '@instamenu/context-user';
import { useDataFilters } from './use-data-filters';

export default function RestaurantSelection() {
  const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });
  const [visible, setVisible] = useState(false);
  const { i18n } = useLocale();
  const { sortedRestaurants } = useUserContext();
  const { restaurants: selection, setRestaurants: onChange } = useDataFilters();

  const handleSubmit: FormEventHandler = (e) => {
    e?.preventDefault();
    setVisible(false);
  };
  const allSelected = useMemo(
    () => selection.length === sortedRestaurants.length,
    [selection, sortedRestaurants],
  );

  const RestaurantList = (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        width: '100%',
        maxHeight: isDesktop ? '370px' : '80dvh',
      }}
    >
      <div style={{ flexShrink: 1, overflowY: 'scroll' }}>
        {sortedRestaurants.map((restaurant) => (
          <Flex className={styledRow} key={restaurant.id}>
            <Flex className={styledItem} key={restaurant.id}>
              <CheckboxWithLabel
                id={`${restaurant.id}-checkbox`}
                checked={!!selection.find((r) => r.id === restaurant.id)}
                onChange={() => {
                  if (selection.find((r) => r.id === restaurant.id)) {
                    onChange(selection.filter((selected) => selected.id !== restaurant.id));
                  } else {
                    onChange([...selection, restaurant]);
                  }
                }}
                label={restaurant.name}
              />
            </Flex>
            <Button
              onClick={() => onChange([restaurant])}
              css={{ alignContent: 'flex-end' }}
              variant="secondary"
              size="xs"
            >
              {i18n.t('general.restaurantList.only')}
            </Button>
          </Flex>
        ))}
        <Flex className={styledRow}>
          <Flex className={styledItem} as="label">
            <CheckboxWithLabel
              checked={allSelected}
              onChange={() => {
                if (allSelected) {
                  onChange([]);
                } else {
                  onChange([...sortedRestaurants]);
                }
              }}
              label={i18n.t('general.restaurantList.selectAll')}
            />
          </Flex>
        </Flex>
      </div>
      <div style={{ display: isDesktop ? 'none' : 'block' }}>
        <Button
          type="submit"
          style={{
            width: '100%',
            margin: '48px 0',
            flexGrow: 0,
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          {i18n.t('general.restaurantList.done')}
        </Button>
      </div>
    </form>
  );

  return (
    <>
      <DrawerDialog
        visible={visible}
        setVisible={setVisible}
        label={
          selection.length === 0
            ? i18n.t('general.restaurantList.selectLocation')
            : selection.length === 1
              ? selection[0].name
              : i18n.t('general.restaurantList.locations', { selectionLength: selection.length })
        }
      >
        {RestaurantList}
      </DrawerDialog>
    </>
  );
}
